import { useHistory } from "react-router-dom";
import { usePageFlip } from "../../../components/app/PageFlip";
import { useActiveLang } from "../../../domains/app/hooks/useActiveLang";
import { usePageLinks } from "../../../domains/app/hooks/usePageLinks";
import { useArticleSwipeCache } from "../stores/useArticleSwipeCache";
import { useEffect } from "react";

export function useArticleSwipe(slug: string = "") {
  const history = useHistory();
  const { isRtl } = useActiveLang();
  const {
    docs: swipeList,
    index: swipeIndex,
    setDocs: setSwipeList,
    setIndex: setSwipeIndex,
    clear: clearSwipeCache,
  } = useArticleSwipeCache();
  const currentSwipeArticle = swipeList.find(
    (article) => article.slug === slug
  );
  const { flipDirection, flipLeft, flipRight } = usePageFlip();
  const { articleLink } = usePageLinks(null);

  // Restore swipe index if found in cache
  // Callbacks (setSwipeIndex, clearSwipeCache) are unstable but intentionally omitted

  useEffect(() => {
    if (currentSwipeArticle && swipeList.length > 0) {
      const swipeListSlugs = swipeList.map((article) => article.slug);
      const slugIndex = swipeListSlugs.indexOf(slug);
      if (slugIndex !== -1) {
        setSwipeIndex(slugIndex);
      }
    } else if (swipeList.length > 0 && !currentSwipeArticle) {
      clearSwipeCache();
    }
  }, [currentSwipeArticle, swipeList, slug]);

  function swipePrevious() {
    if (swipeList.length > 0 && swipeIndex !== -1 && swipeIndex > 0) {
      const prevArticle = swipeList.at(swipeIndex - 1);
      if (prevArticle === undefined) return;
      history.replace(articleLink(prevArticle));
      setSwipeIndex(swipeIndex - 1);
      isRtl ? flipLeft() : flipRight();
    }
  }

  function swipeNext() {
    if (
      swipeList.length > 0 &&
      swipeIndex !== -1 &&
      swipeIndex < swipeList.length - 1
    ) {
      const nextArticle = swipeList.at(swipeIndex + 1);
      if (nextArticle === undefined) return;
      history.replace(articleLink(nextArticle));
      setSwipeIndex(swipeIndex + 1);
      isRtl ? flipRight() : flipLeft();
    }
  }

  return {
    swipeList,
    swipeIndex,
    swipeDirection: flipDirection,
    currentSwipeArticle,
    setSwipeList,
    setSwipeIndex,
    swipePrevious,
    swipeNext,
    clearSwipeCache,
  };
}
